import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useToggle } from "react-use";
import { Button, Caption1, Checkbox, ComboBox, Dialog, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import startCase from "lodash/startCase";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { CustomFormField } from "~/components/CustomFormField.jsx";
import { MEMBER_PERMISSIONS } from "~/records/record.project";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { fetchProjectsDetails } from "~/store/actions/actions.projects";
import { getPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getLoggedUser } from "~/store/selectors/selector.users";
import { isMemberWithoutPermissions } from "~/utils/userHelper";
import { MoveCopyToProjectFormValidator } from "./MoveCopyToProjectFormValidator";

const MoveCopyToProjectDialog = ({
  open,
  title,
  explainer,
  submitButtonTitle,
  cancelButtonTitle,
  onSubmit,
  onCancel,
  excludeIds,
  type,
}) => {
  const { t } = useTranslation();
  const checkboxId = useMemo(v4, []);
  const dispatch = useDispatch();

  const redirectSelector = useSelector((state) => getPersonalizedConfigOption(state, "redirect_on_move"));

  const [stay, toggle] = useToggle(!redirectSelector);

  const [projects, setProjects] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const loggedUser = useSelector(getLoggedUser);

  useEffect(() => {
    dispatch(fetchProjectsDetails()).then((data) => {
      setIsFetching(false);
      let userProjects = data.response.entities.projects;
      const filteredProjects = Object.values(userProjects).reduce((acc, project) => {
        if (
          !isMemberWithoutPermissions(loggedUser) ||
          project.member_permissions === MEMBER_PERMISSIONS.COLLABORATIVE ||
          project.leader_id === loggedUser.id ||
          (type !== "Task" && type !== "Task List")
        ) {
          acc[project.id] = project.name;
        }
        return acc;
      }, {});

      setProjects(filteredProjects);
    });
  }, [dispatch, loggedUser, type]);

  const options = projects
    ? Object.entries(projects)
        .map(([id, name]) => ({ id: Number(id), name }))
        .filter((option) => option.id !== excludeIds[0])
    : [];

  const handleSubmit = useCallback(
    async ({ project_id, copy }) => {
      if (stay === redirectSelector) {
        dispatch(
          editPersonalizedConfigOptions({
            redirect_on_move: !stay,
          })
        );
      }
      typeof onSubmit === "function" && (await onSubmit({ project_id, copy, redirect: !stay }));
    },
    [dispatch, onSubmit, redirectSelector, stay]
  );

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <Formik
        initialValues={{
          project_id: null,
          copy: false,
        }}
        validationSchema={MoveCopyToProjectFormValidator}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ isValid, isSubmitting }) => (
          <Form>
            <Dialog.Title>
              {title
                ? title
                : t("Move {{type}} to Project", {
                    type: startCase(type),
                  })}
            </Dialog.Title>
            <Dialog.ContentDivider />
            <Dialog.Content>
              <div className="tw-mb-6">
                <CustomFormField name="project_id">
                  {({ field }) => (
                    <ComboBox
                      loading={isFetching}
                      selected={field.value}
                      options={options}
                      onChange={field.onChange}
                      placeholder={`${t("Select project")}...`}
                      size="big"
                      noResultText={t("No Projects")}
                      autoFocus
                    />
                  )}
                </CustomFormField>
              </div>
              <div className="tw-mb-6">
                <div className="tw-flex tw-flex-row tw-items-center">
                  <Field name="copy">
                    {({ field }) => (
                      <Checkbox
                        {...field}
                        className="tw-mr-2"
                        id={checkboxId}
                        checked={field.value}
                      />
                    )}
                  </Field>
                  <Label
                    size="regular"
                    htmlFor={checkboxId}
                    className="tw-cursor-pointer"
                    weight="regular"
                  >
                    {t("Leave a copy of this {{type}} in this project", {
                      type: type.toLowerCase(),
                    })}
                  </Label>
                </div>
                {explainer?.length > 0 ? (
                  <Caption1
                    color="tertiary"
                    className="tw-mt-1"
                  >
                    {explainer}
                  </Caption1>
                ) : null}
              </div>
              <div className="tw-flex tw-flex-row tw-items-center">
                <Checkbox
                  className="tw-mr-2"
                  id="redirect"
                  checked={stay}
                  onChange={toggle}
                />
                <Label
                  size="regular"
                  htmlFor="redirect"
                  className="tw-cursor-pointer"
                  weight="regular"
                >
                  {t("Continue working on the current project")}
                </Label>
              </div>
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                type="submit"
                variant="primary"
                data-qa-id="confirm-modal-submit"
                disabled={!isValid || isSubmitting}
                className="tw-mr-3"
              >
                {submitButtonTitle ? submitButtonTitle : t("Move")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
              >
                {cancelButtonTitle ? cancelButtonTitle : t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

MoveCopyToProjectDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  explainer: PropTypes.string,
  className: PropTypes.string,
  currentProjectId: PropTypes.number,
  submitButtonTitle: PropTypes.string,
  cancelButtonTitle: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  excludeIds: PropTypes.arrayOf(PropTypes.number),
  type: PropTypes.string,
};

MoveCopyToProjectDialog.defaultProps = {
  open: false,
  className: "",
  currentProjectId: 0,
  excludeIds: [],
  type: "Task",
};

export default MoveCopyToProjectDialog;
